import { useEffect, useMemo, useState } from "react";

// Interfaces
import { ICitActivity, IReducedActivity } from "@/Common/types/ContractsInTransit/model";
import { ICitContentDetails } from "./CitContentDetails/interfaces";
import { ICitExportProps, IContentInformation, TActivityType } from "./interfaces";

// Helpers
import { getFilteredActivities } from "./helpers";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Components
import CitContentDetails from "./CitContentDetails";

// MUI
import { Grid } from "@mui/material";
import { DATE_FORMAT } from "@/Constants/date";

const getActivityTypeByIndex = (optionsDetails: IContentInformation[] = [], index = 0) => {
	return optionsDetails[index]?.activityType ?? "Finance Reserve";
};

export default function CitExport(props: Readonly<ICitExportProps>) {
	const { loading = false, activities = [], exportHistory = [], optionsDetails = [], onActionUpdate = () => {} } = props ?? {};

	const tabsLabel = optionsDetails.map((tab) => tab.label);
	const activityTypes: TActivityType[] = optionsDetails.map((tab) => tab.activityType);

	// States
	const [options, setOptions] = useState<IContentInformation[]>([]);
	const [tabIndex, setTabIndex] = useState(0);

	const activeContentType = getActivityTypeByIndex(optionsDetails, tabIndex);

	const handleOnTabChange = (index: number) => {
		setTabIndex(index);
	};

	const handleExcludedExport = (value: boolean) => {
		const update = [...options];
		update[tabIndex] = { ...update[tabIndex], excludedExport: value };
		if (value) {
			update[tabIndex]["activitiesIds"] = [];
		}
		setOptions(update);
		onActionUpdate(update);
	};

	const handleUpdateDetails = (updated: ICitContentDetails, index: number) => {
		const update = [...options];
		update[index] = { ...update[index], ...updated };
		setOptions(update);
		onActionUpdate(update);
	};

	const mappedActivities = useMemo(() => {
		if (!activities?.length) {
			return {};
		}
		return activityTypes.reduce(
			(acc, activityType: TActivityType) => {
				acc[activityType] = getFilteredActivities(activities, activityType, "activityType");
				return acc;
			},
			{} as { [key: string]: ICitActivity[] }
		);
	}, [activityTypes, activities]);

	useEffect(() => {
		setOptions(optionsDetails);
	}, [optionsDetails]);

	const selectableExportHistory = useMemo(() => {
		const filteredExportHistory = tabIndex
			? exportHistory.filter((item) => item.exportType?.includes("cit"))
			: exportHistory.filter((item) => item.exportType?.includes("rsrv"));

		const groupedActivities: Record<string, IReducedActivity[]> = {};
		const possibleExportsMap: Record<string, { id: string; label: string }> = {};

		for (let index = 0; index < filteredExportHistory.length; index++) {
			const item = filteredExportHistory[index];
			const { exportId, activities } = item;
			if (!exportId) continue;

			// Group activities by exportId
			if (!groupedActivities[exportId]) groupedActivities[exportId] = [];
			groupedActivities[exportId].push(...activities);

			// Find the first activity that has an "exported" field
			const exportedActivity = activities.find((a) => a.exported);
			if (exportedActivity) {
				const exportedBy = exportedActivity.exported.exportedBy;

				const exportedOn = getFormattedDate(exportedActivity.exported.exportedOn, "", DATE_FORMAT.abbrMonthDayTime);

				possibleExportsMap[exportId] = {
					id: exportId,
					label: `${exportedBy} ${exportedOn}`,
				};
			}
		}

		const possibleExports = Object.values(possibleExportsMap);

		return { groupedActivities, possibleExports };
	}, [exportHistory, tabIndex]);

	const isCheckboxDisabled = options[tabIndex]?.disabled || !mappedActivities?.[activeContentType]?.length;
	// MMM/DD hh:mm A
	return (
		<Grid container>
			<Grid item xs={12}>
				{loading ? (
					<Grid>
						<TabLoader tabs={2} switch bottomSwitch />
					</Grid>
				) : (
					<TabSelectable
						tabs={tabsLabel}
						tabSelected={tabIndex}
						onTabChange={handleOnTabChange}
						errors={[]}
						content={Object.values(options).map((details: IContentInformation, index: number) => {
							const { key, ...properties } = details;
							const typeActivities = mappedActivities[properties.activityType] ?? [];
							return (
								<CitContentDetails
									{...properties}
									key={key}
									identifier={key}
									isShowing={tabIndex === index}
									activitiesIds={options[index]?.activitiesIds ?? []}
									activities={typeActivities}
									selectableExportHistory={selectableExportHistory}
									onUpdateAction={(updated) => handleUpdateDetails(updated, index)}
								/>
							);
						})}
						bottomActions={[
							<CustomizedSwitches
								key={"exportExcluded"}
								textChecked={"Exclude from export"}
								textUnChecked={"Exclude from export"}
								checked={options[tabIndex]?.excludedExport ?? false}
								disabled={isCheckboxDisabled}
								onChange={handleExcludedExport}
							/>,
						]}
					/>
				)}
			</Grid>
		</Grid>
	);
}
